import { NavLink } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import api, { clearToken } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import {
  LayoutDashboard,
  Users,
  Radio,
  Replace,
  ImagePlus,
  ListChecks,
  ListTree,
  FlaskConical,
  Bell,
  LogOut,
  Archive,
  ScanSearch,
  X,
} from "lucide-react";

const links = [
  { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview", end: true },
  { to: "/accounts", label: "Accounts", icon: Users, testid: "nav-accounts" },
  { to: "/channels", label: "Channels", icon: Radio, testid: "nav-channels" },
  { to: "/channel-scanner", label: "Channel Scanner", icon: ScanSearch, testid: "nav-scanner" },
  { to: "/rules", label: "Replacements", icon: Replace, testid: "nav-rules" },
  { to: "/branding", label: "Branding", icon: ImagePlus, testid: "nav-branding" },
  { to: "/sandbox", label: "Sandbox", icon: FlaskConical, testid: "nav-sandbox" },
  { to: "/jobs", label: "Copy Jobs", icon: ListChecks, testid: "nav-jobs" },
  { to: "/vault", label: "Content Vault", icon: Archive, testid: "nav-vault" },
  { to: "/queue", label: "Job Queue", icon: ListTree, testid: "nav-queue" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications" },
];

export const Sidebar = ({ open = false, onClose = () => {} }) => {
  const [unread, setUnread] = useState(0);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts");
      setUnread(data.unread || 0);
    } catch (e) {
      // silent — auth/transient
    }
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    const onRead = () => setUnread(0);
    window.addEventListener("clonecast:alerts-read", onRead);
    return () => {
      clearInterval(t);
      window.removeEventListener("clonecast:alerts-read", onRead);
    };
  }, [loadUnread]);

  return (
    <aside
      data-testid="sidebar"
      className={`surface-dark w-64 shrink-0 border-r border-[var(--tg-rail-border)] bg-[var(--tg-rail)] flex flex-col h-screen fixed top-0 left-0 z-50 transition-transform duration-300 md:sticky md:z-20 md:translate-x-0 ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="relative h-16 flex items-center px-4 border-b border-[var(--tg-rail-border)]">
        <BrandLogo className="w-[185px] md:w-[216px]" />
        <button
          onClick={onClose}
          data-testid="sidebar-close"
          className="absolute right-2 md:hidden text-[var(--tg-rail-muted)] hover:text-[var(--tg-rail-ink)] transition-colors"
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-2">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            data-testid={l.testid}
            onClick={onClose}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-3 min-h-11 rounded-md text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-[rgb(var(--c-sky-rgb)/0.18)] text-[var(--tg-rail-ink)] border border-[rgb(var(--c-sky-rgb)/0.45)] shadow-[inset_3px_0_0_var(--c-sky)]"
                  : "text-[var(--tg-rail-muted)] hover:text-[var(--tg-rail-ink)] hover:bg-[var(--tg-rail-hover)] border border-transparent"
              }`
            }
          >
            <l.icon className="h-4 w-4" />
            {l.label}
            {l.testid === "nav-notifications" && unread > 0 && (
              <span
                className="ml-auto text-[10px] leading-4 font-bold px-1.5 rounded-full bg-[var(--c-rose)] text-[var(--tg-rail-ink)] min-w-[20px] h-5 grid place-items-center text-center"
                data-testid="nav-alerts-badge"
              >
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[var(--tg-rail-border)]">
        <button
          onClick={() => {
            clearToken();
            window.dispatchEvent(new Event("clonecast:unauthorized"));
          }}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-[var(--tg-rail-muted)] hover:text-[var(--c-rose)] hover:bg-[rgb(var(--c-rose-rgb)/0.10)] transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Lock Dashboard
        </button>
      </div>
    </aside>
  );
};
